import { HTMLThemeElement } from '@archetype-themes/custom-elements/theme-element'
import { EVENTS } from '@archetype-themes/utils/events'
import Modals from '@archetype-themes/modules/modal'
import { EnabledDesigns, DesignSwatches, designOptionsConfig, DesignOptions, Designs, DesignLabels } from './custom-engraving-constants.js'
import EngravingCanvas from './custom-engraving-canvas.js'

class CustomEngravingPopup extends HTMLThemeElement {
  connectedCallback() {
    super.connectedCallback()

    if (!this) return

    const customPotSelector = document.querySelector('input[value="Custom Pot"]+.variant__button-label-wrapper > label')

    if (!customPotSelector) { return }

    this.modal = new Modals(this.getAttribute('id'), 'custom-engraving', {
      trapFocus: !Shopify.designMode
    })

    this.customOptionsWrapper = this.querySelector('.js-engraving-custom-options');
    this.customTextInput = this.querySelector('.js-engraving-custom-text');
    this.customTextInput2 = this.querySelector('.js-engraving-custom-text-2');
    this.designInputCtr = this.querySelector('.js-engraving-design-options');
    this.dateInputCtr = this.querySelector('.js-engraving-date-picker');
    this.potColorsEl = this.querySelector('.js-engraving-color-options');
    this.selectedDesignInfoEl = this.querySelector('.js-selected-engraving-design');
    this.selectedPotColorEl = this.querySelector('.js-selected-engraving-pot-color')
    this.form = this.querySelector('.custom-engraving-form')
    this.atcBtn = this.querySelector('.js-engraving-atc')

    this.initEngravingExperience()
  }

  async initEngravingExperience() {
    this.addListeners()
    this.updateColorOptions()
    this.initDesignOptions()
    this.canvas = await EngravingCanvas(this.querySelector('.engraving-visualizer__canvas'))

    this.picker = new easepick.create({
      element: this.dateInputCtr,
      format: "MM.DD.YYYY",
      plugins: ['AmpPlugin'],
      AmpPlugin: {
        dropdown: {
          months: true,
          years: true,
        },
        resetButton: true
      },
      css: [
        'https://cdn.jsdelivr.net/npm/@easepick/core@1.2.1/dist/index.css',
      ],
    });

    this.picker.on('select', this.refresh)
    this.picker.on('clear', this.refresh)

    this.refresh()
  }

  updateColorOptions() {
    const selectedPotSize = document.querySelector('[data-option="product-option-size"] input:checked').value
    this.potColorsEl.querySelectorAll('.engraving-visualizer-option').forEach(colorOption => {
      colorOption.classList.toggle('visually-hidden', colorOption.dataset.potSize !== selectedPotSize)
    })

    // Select first visible option
    const firstColorOption = this.potColorsEl.querySelector('.engraving-visualizer-option:not(.visually-hidden)')

    if (firstColorOption) {
      firstColorOption.querySelector('input').checked = true
      this.selectedPotColorEl.innerText = firstColorOption.querySelector('input').dataset.title
    }
  }

  initDesignOptions() {
    let markup = '';

    EnabledDesigns.forEach((design, index) => {
      const swatch = DesignSwatches[design];

      markup += `
        <div class="engraving-design-option">
          <input type="radio" name="_engravedDesign" id="${design}" value="${design}" ${index == 0 ? 'checked="checked"': ''}>
          <label for="${design}">
            <img src="${swatch}" />
          </label>
        </div>
      `;
    });

    this.designInputCtr.innerHTML = markup;
  }

  parseDateValue() {
    return this.dateInputCtr.value || ''
  }

  getEngravingConfig = () => {
    return {
      potColor: this.potColorsEl.querySelector('input:checked').value,
      engravingDesign: this.designInputCtr.querySelector('input:checked').value,
      customText: this.customTextInput.value,
      customText2: this.customTextInput2.value,
      customDate: this.parseDateValue()
    }
  }

  addListeners() {
    const variantPicker = document.querySelector('.block-variant-picker');
    const customPotSelector = document.querySelector('input[value="Custom Pot"]+.variant__button-label-wrapper > label')

    if (!customPotSelector) {
      return
    }

    this.abortController = new AbortController()

    this.refresh = this.refresh.bind(this)
    this.handleDesignChange = this.handleDesignChange.bind(this)
    this.handleTextInput = this.handleTextInput.bind(this)
    this.handleTextInputFocus = this.handleTextInputFocus.bind(this)
    this.handlePotColorChange = this.handlePotColorChange.bind(this)
    this.enableEngravingExperience = this.enableEngravingExperience.bind(this)
    this.handleVariantChange = this.handleVariantChange.bind(this)
    this.handleFormSubmit = this.handleFormSubmit.bind(this)

    document.addEventListener(
      `${EVENTS.variantChange}:${this.dataset.sectionId}:${this.dataset.productId}`,
      this.handleVariantChange,
      {
        signal: this.abortController.signal
      }
    )

    customPotSelector.addEventListener('click', this.enableEngravingExperience, {
        signal: this.abortController.signal
      }
    );

    this.designInputCtr.addEventListener('change', this.handleDesignChange, {
      signal: this.abortController.signal
    });
    this.customTextInput.addEventListener('input', this.refresh, {
      signal: this.abortController.signal
    });
    this.customTextInput.addEventListener('focus', this.handleTextInputFocus, {
      signal: this.abortController.signal
    });
    this.customTextInput2.addEventListener('focus', this.handleTextInputFocus, {
      signal: this.abortController.signal
    });
    this.customTextInput2.addEventListener('input', this.refresh, {
      signal: this.abortController.signal
    });
    this.potColorsEl.addEventListener('change', this.handlePotColorChange, {
      signal: this.abortController.signal
    });

    this.atcBtn.addEventListener('click', this.handleFormSubmit, {
      signal: this.abortController.signal
    })
  }

  handleVariantChange({ detail }) {
    const { variant } = detail;

    if (variant.options[1] === "Custom Pot") {
      this.enableEngravingExperience()
    }
  }

  enableEngravingExperience() {
    this.updateColorOptions()
    this.refresh()
    this.modal.open()
  }

  updateCustomisationOptions(selectedDesign) {
    const config = designOptionsConfig[selectedDesign];

    if (config === DesignOptions.DATE_AND_TEXT) {
      this.customOptionsWrapper.classList.remove('hide');
      this.customTextInput.classList.remove('hide');
      this.customTextInput2.classList.add('hide');
      this.dateInputCtr.classList.remove('hide');
    } else if (config === DesignOptions.DATE_ONLY) {
      this.customOptionsWrapper.classList.remove('hide');
      this.customTextInput.classList.add('hide');
      this.customTextInput2.classList.add('hide');
      this.dateInputCtr.classList.remove('hide');
    } else if (config === DesignOptions.CUSTOM) {
      this.customOptionsWrapper.classList.remove('hide');
      this.customTextInput.classList.remove('hide');
      this.customTextInput2.classList.remove('hide');
      this.dateInputCtr.classList.remove('hide');
    } else if (config === DesignOptions.NONE) {
      this.customTextInput.value = '';
      this.customTextInput2.value = '';
      this.dateInputCtr.value = '';

      this.customOptionsWrapper.classList.add('hide');
      this.customTextInput.classList.add('hide');
      this.customTextInput2.classList.add('hide');
      this.dateInputCtr.classList.add('hide');
    }
  }

  refresh() {
    this.canvas.refreshDesignAndText(this.getEngravingConfig())
  }

  handleDesignChange(e) {
    const selectedDesign = e.target.value;

    if (!this.customTextInput.hasAttribute('is-dirty')) {
      if (selectedDesign !== Designs.CUSTOM_TEXT) {
        this.customTextInput.value = '';
      } else {
        this.customTextInput.value = 'Sample Text 1';
      }
    }

    this.selectedDesignInfoEl.innerText = DesignLabels[selectedDesign];
    this.updateCustomisationOptions(selectedDesign);
    this.refresh()
  }

  handleTextInput() {

  }

  handlePotColorChange() {
    // ToDo: No pots to test this with yet
  }

  async handleFormSubmit(e) {
    e.preventDefault()
    e.stopPropagation()

    const formData = new FormData()

    formData.set('id',  document.querySelector('[name="id"]').value)
    const engravingDesign = this.designInputCtr.querySelector('input:checked').value
    let engravingSummary = `Design: ${engravingDesign}`
    const engravingDate = this.parseDateValue()

    formData.set('properties[_engravedDesign]', engravingDesign)

    if (this.customTextInput.value && !this.customTextInput.value.includes('Sample Text')) {
      formData.set('properties[_engravedText]', this.customTextInput.value)
      engravingSummary += `, Text: ${this.customTextInput.value}`
    }

    if (this.customTextInput2.value && !this.customTextInput2.value.includes('Sample Text')) {
      formData.set('properties[_engravedText2]', this.customTextInput2.value)
      engravingSummary += `, Text 2: ${this.customTextInput2.value}`;
    }

    if (engravingDate) {
      formData.set('properties[_engravedDate]', engravingDate)
      engravingSummary += `, Date: ${engravingDate}`;
    }

    formData.set('properties[Engraving]', engravingSummary)
    formData.set('sections', 'cart-ajax')

    this.atcBtn.classList.add('btn--loading')

    const response = await (await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })).json()

    this.atcBtn.classList.remove('btn--loading')

    this.dispatchEvent(
      new CustomEvent(EVENTS.ajaxProductAdded, {
        bubbles: true,
        detail: {
          product: response
        }
      })
    )
  }

  handleTextInputFocus(e) {
    const customTextInput = e.target;

    if (!customTextInput.hasAttribute('is-dirty')) {
      customTextInput.value = '';
      customTextInput.setAttribute('is-dirty', 'true');
      this.refresh();
    }
  }
}

customElements.define('custom-engraving', CustomEngravingPopup)